import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import "./api/fetchInterceptor"; // fetch() için global 401/402 interceptor
import { setSessionModalActive } from "./store/auth/login/actions";

// Tüm axios istekleri için cookie desteğini etkinleştir
axios.defaults.withCredentials = true;

// Eski session'dan kalmış raw token'ları temizle (artık HttpOnly cookie'de)
localStorage.removeItem("token");
localStorage.removeItem("refreshToken");

// Eski yapıdaki authUser'ı temizle (cookie migration öncesi format)
// Yeni format: { loginType, email, loginTime, ... } — loginType yoksa eski format
try {
  const rawAuthUser = localStorage.getItem("authUser");
  if (rawAuthUser) {
    const parsed = JSON.parse(rawAuthUser);
    if (!parsed.loginType) {
      localStorage.removeItem("authUser");
      localStorage.removeItem("tokenExp");
    }
  }
} catch {
  localStorage.removeItem("authUser");
}

// Global request interceptor: Authorization header'ı her istekten tamamen kaldır.
// Token artık HttpOnly cookie'de; withCredentials: true ile tarayıcı otomatik gönderir.
// Manuel Bearer göndermek backend'in cookie'yi okumasını engeller.
axios.interceptors.request.use(config => {
  if (config.headers) {
    delete config.headers.Authorization;
  }
  return config;
});
import './assets/css/custom.css';
// Contract imports
import contractInterceptor from './routes/middleware/ContractInterceptor';
import ContractModal from './pages/modals/contract/ContractModal';

// Rotaları içe aktar
import { userRoutes, authRoutes } from "./routes/allRoutes";

// Middleware'leri içe aktar
import Authmiddleware from "./routes/middleware/Authmiddleware";

// Abonelik route kapısı (EXPIRED erişim kısıtlama)
import SubscriptionGate from "./components/Common/SubscriptionGate";

// Layout'ları içe aktar
import ErrorBoundary from "./components/Common/ErrorBoundary";
import VerticalLayout from "./components/VerticalLayout/";
import HorizontalLayout from "./components/HorizontalLayout/";
import NonAuthLayout from "./components/NonAuthLayout";

// Scss dosyalarını içe aktar
import "./assets/scss/theme.scss";
import "./assets/scss/preloader.scss";

// Diğer yardımcıları içe aktar
import fakeBackend from "./helpers/AuthType/fakeBackend";
import PreventAuthRoute from "./routes/middleware/PreventAuthRoute";
import { ToastContainer } from "react-toastify";
import {API} from "./api/urls"
import { logout, refreshToken } from "./api/requests/auth-requests"

// Fake backend'i etkinleştir
fakeBackend();

/**
 * Oturum yenileme onayı için bir Modal bileşeni.
 */
const TokenRefreshModal = ({ onConfirm, onDecline, expired = false }) => {
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1050,
  };

  const modalContentStyle = {
    backgroundColor: '#fff',
    padding: '30px 50px',
    borderRadius: '8px',
    textAlign: 'center',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    color: '#333',
    maxWidth: '400px'
  };

  const buttonContainerStyle = { marginTop: '20px' };

  const buttonStyle = {
    margin: '0 10px',
    padding: '10px 24px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px',
    transition: 'transform 0.2s',
  };

  return (
      <div style={modalOverlayStyle}>
        <div style={modalContentStyle}>
          <h4 className="mb-3">
            {expired ? "Oturum Süreniz Doldu" : "Oturum Süreniz Dolmak Üzere"}
          </h4>
          <p>
            {expired
                ? "Devam etmek için oturumunuzu şimdi yenilemek ister misiniz?"
                : "Uygulamada kalmaya devam etmek istiyor musunuz?"}
          </p>
          <div style={buttonContainerStyle}>
            <button
                style={{ ...buttonStyle, backgroundColor: '#28a745', color: 'white' }}
                onClick={onConfirm}
                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              {expired ? "Yenile" : "Evet"}
            </button>
            <button
                style={{ ...buttonStyle, backgroundColor: '#dc3545', color: 'white' }}
                onClick={onDecline}
                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              {expired ? "Çıkış Yap" : "Hayır"}
            </button>
          </div>
        </div>
      </div>
  );
};

TokenRefreshModal.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
  expired: PropTypes.bool
};

const App = (props) => {
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  // Kullanıcının son aktivite zamanı (re-render tetiklememek için ref).
  const lastActivityRef = useRef(Date.now());
  // Eşzamanlı çift sessiz-yenilemeyi önlemek için kilit.
  const autoRefreshingRef = useRef(false);

  // Contract modal state
  const [contractModalVisible, setContractModalVisible] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  const updateSessionModalState = useCallback((isActive) => {
    setShowRefreshModal(isActive);
    props.setSessionModalActive(isActive);
  }, [props.setSessionModalActive]);

  // Contract interceptor setup
  useEffect(() => {
    contractInterceptor.setContractModalCallback((data) => {
      setContractData(data);
      setContractModalVisible(true);
    });
  }, []);

  // Kullanıcıyı sistemden çıkaran fonksiyon
  const handleLogout = useCallback(async () => {
    // Backend cookie'lerini temizle (HttpOnly olduğundan sadece sunucu silebilir)
    try {
      await logout();
    } catch {}
    localStorage.removeItem("authUser");
    localStorage.removeItem("tokenExp");
    updateSessionModalState(false);
    setIsExpired(false);
    setContractModalVisible(false);
    setContractData(null);
    navigate("/login");
  }, [navigate, updateSessionModalState]);

  // Backend'e istek atarak token'ı yenileyen fonksiyon
  // Token artık HttpOnly cookie'de; body'ye refresh token göndermeden cookie ile refresh yapılır
  const handleRefreshToken = useCallback(async () => {
    try {
      const response = await refreshToken();

      if (response.ok) {
        const result = await response.json();
        if (result?.data?.accessToken) {
          // Yeni tokenın exp'ini sakla (client-side expiry check için)
          try {
            const decoded = jwtDecode(result.data.accessToken);
            if (decoded.exp) localStorage.setItem("tokenExp", decoded.exp);
          } catch {}
          updateSessionModalState(false);
          setIsExpired(false);
        } else {
          throw new Error("Refresh endpointinden geçersiz yanıt.");
        }
      } else {
        throw new Error("Token yenileme başarısız oldu.");
      }
    } catch (error) {
      console.error(error.message);
      handleLogout();
    }
  }, [handleLogout, updateSessionModalState]);

  // Contract modal handlers
  const handleContractSigned = useCallback(() => {
    setContractModalVisible(false);
    setContractData(null);
    setRefreshTrigger(prev => prev + 1);
    navigate("/dashboard");
    // OnboardingTour'u bilgilendir: sözleşme imzalandı, tur başlayabilir
    window.dispatchEvent(new CustomEvent("contractSigned"));
  }, [navigate]);

  const handleContractModalClose = useCallback(() => {
    handleLogout();
  }, [handleLogout]);

  // Kullanıcı aktivitesini izle (re-render yok; sadece ref güncellenir).
  useEffect(() => {
    const markActive = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((ev) => window.addEventListener(ev, markActive, { passive: true }));
    return () => events.forEach((ev) => window.removeEventListener(ev, markActive));
  }, []);

  useEffect(() => {
    // Sadece kimlik doğrulaması gerektiren sayfalarda kontrol yap
    const isProtectedRoute = userRoutes.some(route =>
        location.pathname.startsWith(route.path.split('/:')[0]) && route.path !== '/*'
    );
    if (!isProtectedRoute) return;

    // Token süresinin dolmasına bu kadar kala (dk) aksiyon alınır.
    const WARN_MINUTES = 10;
    // Kullanıcı son bu kadar süre (ms) içinde aktifse "aktif" sayılır → sessiz yenile.
    const ACTIVE_WINDOW_MS = 10 * 60 * 1000;

    const checkTokenExpiration = () => {
      // Token HttpOnly cookie'de olduğundan JS okuyamaz.
      // Login/refresh sırasında saklanan tokenExp (Unix saniye) kullanılır.
      const tokenExpRaw = localStorage.getItem("tokenExp");
      if (!tokenExpRaw) return;

      const tokenExp = Number(tokenExpRaw);
      if (Number.isNaN(tokenExp)) return;

      const nowSec = Math.floor(Date.now() / 1000);
      const secondsRemaining = tokenExp - nowSec;

      if (secondsRemaining <= 0) {
        if (!showRefreshModal) {
          setIsExpired(true);
          updateSessionModalState(true);
        }
        return;
      }

      const minutesRemaining = secondsRemaining / 60;
      if (minutesRemaining > WARN_MINUTES) return;

      // Modal zaten açıksa tekrar müdahale etme (kullanıcı karar veriyor).
      if (showRefreshModal) return;

      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs < ACTIVE_WINDOW_MS) {
        // Kullanıcı aktif → modal göstermeden arka planda sessizce yenile.
        if (!autoRefreshingRef.current) {
          autoRefreshingRef.current = true;
          Promise.resolve(handleRefreshToken()).finally(() => {
            autoRefreshingRef.current = false;
          });
        }
        return;
      }

      // Kullanıcı boşta → onay modalını göster.
      setIsExpired(false);
      updateSessionModalState(true);
    };

    const intervalId = setInterval(checkTokenExpiration, 60000);
    return () => clearInterval(intervalId);
  }, [showRefreshModal, location.pathname, updateSessionModalState, handleRefreshToken]);

  function getLayout() {
    let layoutCls = VerticalLayout;
    switch (props.layout.layoutType) {
      case "horizontal":
        layoutCls = HorizontalLayout;
        break;
      default:
        layoutCls = VerticalLayout;
        break;
    }
    return layoutCls;
  }

  const Layout = getLayout();

  return (
      <React.Fragment>
        {/* Token Refresh Modal */}
        {showRefreshModal && (
            <TokenRefreshModal
                expired={isExpired}
                onConfirm={handleRefreshToken}
                onDecline={handleLogout}
            />
        )}

        {/* Contract Modal */}
        {contractModalVisible && (
            <ContractModal
                visible={contractModalVisible}
                onClose={handleContractModalClose}
                contractData={contractData}
                onContractSigned={handleContractSigned}
            />
        )}

        <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            pauseOnFocusLoss
            draggable
            pauseOnHover
            containerId="global-toaster"
        />

        <Routes>
          {authRoutes.map((route, idx) => (
              <Route
                  path={route.path}
                  element={
                    <NonAuthLayout>
                      <PreventAuthRoute>
                        {route.component}
                      </PreventAuthRoute>
                    </NonAuthLayout>
                  }
                  key={idx}
                  exact={true}
              />
          ))}

          {userRoutes.map((route, idx) => (
              <Route
                  path={route.path}
                  element={
                    <Authmiddleware>
                      <Layout key={refreshTrigger} suppressOnboarding={contractModalVisible}>
                        <ErrorBoundary resetKey={location.pathname}>
                          <SubscriptionGate>{route.component}</SubscriptionGate>
                        </ErrorBoundary>
                      </Layout>
                    </Authmiddleware>
                  }
                  key={idx}
                  exact={true}
              />
          ))}
        </Routes>
      </React.Fragment>
  );
};

App.propTypes = {
  layout: PropTypes.any,
  setSessionModalActive: PropTypes.func,
};

const mapStateToProps = (state) => {
  return { layout: state.Layout };
};

const mapDispatchToProps = {
  setSessionModalActive,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);